import { useCallback, useState, useEffect } from 'react';

import { sendAgenticMessage } from '../../api/agenticSearchApi';
import { useLeadData } from '../../contexts/LeadDataContext';
import { useAgenticPreview } from '../../hooks/useAgenticPreview';
import { useExecuteAgenticSearch } from '../../models/prospectQueries/mutations';
import { useAgenticSearchStore } from '../../stores/agenticSearchStore';

import AgenticChatHeader from './AgenticChatHeader';
import AgenticChatInput from './AgenticChatInput';
import AgenticChatMessages from './AgenticChatMessages';
import AgenticChatToggle from './AgenticChatToggle';
import LookalikeSeedPicker from './LookalikeSeedPicker';

const GREETING_MESSAGE =
	'Hallo! Ich helfe Ihnen, die passenden Unternehmen zu finden. Beschreiben Sie einfach, welche Art von Firmen Sie suchen - z.B. Branche, Standort, Gr\u00F6\u00DFe oder besondere Kriterien.';

/**
 * Main container for the agentic search chat widget.
 * Renders as a floating panel anchored to the bottom-right.
 */
const AgenticSearchChat = () => {
	const {
		isOpen,
		setIsOpen,
		messages,
		addMessage,
		isStreaming,
		streamingContent,
		setStreaming,
		appendStreamContent,
		extractedParams,
		setExtractedParams,
		previewResults,
		setPreviewResults,
		isLoadingPreview,
		previewCounts,
		setPreviewCounts,
		phase,
		setPhase,
		resetChat,
		getMessagesForApi,
	} = useAgenticSearchStore();

	const { handleSearchQuerySelect } = useLeadData();
	const executeAgenticSearchMutation = useExecuteAgenticSearch();
	const { loadPreview } = useAgenticPreview();
	const [isExecuting, setIsExecuting] = useState(false);

	// Initialize chat with greeting on first open
	useEffect(() => {
		if (isOpen && phase === 'idle') {
			addMessage({ role: 'assistant', content: GREETING_MESSAGE, type: 'text' });
			setPhase('conversing');
		}
	}, [isOpen, phase, addMessage, setPhase]);

	// Send a user message
	const handleSendMessage = useCallback(
		async (content) => {
			if (isStreaming) return;

			addMessage({ role: 'user', content, type: 'text' });
			setStreaming(true);

			const cleanContent = (text) => text.replace(/<search_params>[\s\S]*?<\/search_params>/g, '').trim();

			let fullResponse = '';
			let receivedParams = null;

			await sendAgenticMessage({
				messages: getMessagesForApi(),
				content,
				onChunk: (chunk) => {
					fullResponse += chunk;
					appendStreamContent(chunk);
				},
				onSearchParams: (params) => {
					receivedParams = params;
					setExtractedParams(params);
				},
				onComplete: () => {
					setStreaming(false);
					const cleaned = cleanContent(fullResponse);
					if (cleaned) {
						addMessage({ role: 'assistant', content: cleaned, type: 'text' });
					}

					if (receivedParams) {
						if (receivedParams.searchType === 'hr') {
							loadPreview(receivedParams);
						} else {
							// Maps: no preview, show direct approve buttons
							setPhase('awaiting_approval');
						}
					}
				},
				onSearchParamsError: (errorMessage) => {
					addMessage({
						role: 'assistant',
						content: `Die Suchparameter konnten nicht verarbeitet werden: ${errorMessage}\n\nBitte passen Sie Ihre Kriterien an.`,
						type: 'text',
					});
				},
				onError: (err) => {
					setStreaming(false);
					console.error('Agentic chat error:', err);
					addMessage({
						role: 'assistant',
						content: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
						type: 'text',
					});
				},
			});
		},
		[
			isStreaming,
			addMessage,
			setStreaming,
			appendStreamContent,
			setExtractedParams,
			loadPreview,
			setPhase,
			getMessagesForApi,
		],
	);

	// User approves → execute full search
	const handleApprove = useCallback(async () => {
		if (!extractedParams || isExecuting) return;

		setIsExecuting(true);
		try {
			// Selecting the new query only sticks once the refetched list contains it —
			// useSearchQueryManagement resets the selection to searchQueries[0] otherwise.
			const result = await executeAgenticSearchMutation.mutateAsync({ searchParams: extractedParams });

			addMessage({
				role: 'assistant',
				content: 'Suche wird gestartet! Die Ergebnisse erscheinen in K\u00FCrze in Ihrer Lead-\u00DCbersicht.',
				type: 'text',
			});

			setPhase('completed');
			setPreviewResults(null);
			handleSearchQuerySelect(result.prospectQueryId);

			setTimeout(() => setIsOpen(false), 1500);
		} catch (err) {
			addMessage({
				role: 'assistant',
				content: `Fehler beim Starten der Suche: ${err.message || 'Unbekannter Fehler'}`,
				type: 'text',
			});
		} finally {
			setIsExecuting(false);
		}
	}, [
		extractedParams,
		isExecuting,
		executeAgenticSearchMutation,
		addMessage,
		setPhase,
		setPreviewResults,
		handleSearchQuerySelect,
		setIsOpen,
	]);

	const handleRefine = useCallback(() => {
		setPreviewResults(null);
		setPreviewCounts(null);
		setExtractedParams(null);
		setPhase('conversing');
		addMessage({
			role: 'assistant',
			content: 'Kein Problem! Was m\u00F6chten Sie \u00E4ndern?',
			type: 'text',
		});
	}, [setPreviewResults, setPreviewCounts, setExtractedParams, setPhase, addMessage]);

	const handleCompanyKindsChange = useCallback(
		(companyKinds) => {
			const params = { ...extractedParams, companyKinds };
			setExtractedParams(params);
			void loadPreview(params);
		},
		[extractedParams, setExtractedParams, loadPreview],
	);

	// Reset conversation
	const handleReset = useCallback(() => {
		resetChat();
		addMessage({ role: 'assistant', content: GREETING_MESSAGE, type: 'text' });
		setPhase('conversing');
	}, [resetChat, addMessage, setPhase]);

	const inputDisabled =
		isStreaming || isLoadingPreview || phase === 'completed' || phase === 'awaiting_approval' || isExecuting;

	return (
		<>
			<AgenticChatToggle />

			{isOpen && (
				<div className="fixed bottom-24 right-6 z-50 w-[480px] h-[550px] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
					<AgenticChatHeader phase={phase} onReset={handleReset} onClose={() => setIsOpen(false)} />

					<AgenticChatMessages
						messages={messages}
						isStreaming={isStreaming}
						streamingContent={streamingContent}
						previewResults={previewResults}
						previewCounts={previewCounts}
						extractedParams={extractedParams}
						onCompanyKindsChange={handleCompanyKindsChange}
						isLoadingPreview={isLoadingPreview}
						onApprove={handleApprove}
						onRefine={handleRefine}
						isExecuting={isExecuting}
						phase={phase}
					/>

					<LookalikeSeedPicker />
					<AgenticChatInput onSend={handleSendMessage} disabled={inputDisabled} />
				</div>
			)}
		</>
	);
};

export default AgenticSearchChat;
